import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Shield,
  Coins,
  Activity,
  Route,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  fetchAdminMe,
  fetchAdminCollections,
  fetchAdminDocuments,
  fetchAdminDocument,
  createAdminDocument,
  updateAdminDocument,
  deleteAdminDocument,
} from '@/lib/adminApi';
import { AdminBillingPanel } from '@/components/admin/AdminBillingPanel';
import { AdminPipelineAuditPanel } from '@/components/admin/AdminPipelineAuditPanel';
import { AdminStoryJourneyPanel } from '@/components/admin/AdminStoryJourneyPanel';
import { AdminCustomerJourneyPanel } from '@/components/admin/AdminCustomerJourneyPanel';

function cellPreview(value) {
  if (value == null) return '—';
  if (typeof value === 'object') {
    const s = JSON.stringify(value);
    return s.length > 80 ? `${s.slice(0, 80)}…` : s;
  }
  const s = String(value);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

function docIdFromRow(row, idField) {
  if (row?.id) return String(row.id);
  if (idField && row?.[idField] != null) return String(row[idField]);
  return '';
}

/** Strip secrets before showing user docs in the admin JSON editor. */
function sanitizeUserDocForEditor(doc) {
  if (!doc || typeof doc !== 'object') return {};
  const out = { ...doc };
  delete out.password;
  delete out.password_hash;
  delete out.pin;
  delete out.pin_hash;
  return out;
}

export function AdminDashboardPage() {
  const [me, setMe] = useState(null);
  const [collections, setCollections] = useState([]);
  const [activeCollection, setActiveCollection] = useState(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editorDocId, setEditorDocId] = useState('');
  const [editorJson, setEditorJson] = useState('{}');
  const [editorSaving, setEditorSaving] = useState(false);
  /** Optional password reset for Users collection — never stored in JSON textarea. */
  const [userNewPassword, setUserNewPassword] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adminView, setAdminView] = useState('collections');

  const perms = me?.permissions || {};
  const idField = activeCollection?.id_field || 'id';
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const loadCollections = useCallback(async () => {
    const data = await fetchAdminCollections();
    setCollections(data.collections || []);
    if (!activeCollection && data.collections?.length) {
      setActiveCollection(data.collections[0]);
    }
  }, [activeCollection]);

  const loadTable = useCallback(async () => {
    if (!activeCollection?.name) return;
    setTableLoading(true);
    try {
      const data = await fetchAdminDocuments(activeCollection.name, {
        page,
        pageSize,
        q: search,
      });
      setRows(data.items || []);
      setColumns(data.columns || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setTableLoading(false);
    }
  }, [activeCollection?.name, page, pageSize, search]);

  useEffect(() => {
    (async () => {
      try {
        const profile = await fetchAdminMe();
        setMe(profile);
        await loadCollections();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Admin access denied');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadCollections]);

  useEffect(() => {
    if (!loading && activeCollection) loadTable();
  }, [loading, activeCollection, loadTable]);

  const openCreate = () => {
    setEditorMode('create');
    setEditorDocId('');
    setUserNewPassword('');
    const seed = idField !== '_id' ? { [idField]: '' } : { _id: '' };
    setEditorJson(JSON.stringify(seed, null, 2));
    setEditorOpen(true);
  };

  const openEdit = async (row) => {
    const docId = docIdFromRow(row, idField);
    if (!docId) {
      toast.error('Missing document id');
      return;
    }
    setEditorMode('edit');
    setEditorDocId(docId);
    setUserNewPassword('');
    setEditorOpen(true);
    setEditorJson('{}');
    try {
      const data = await fetchAdminDocument(activeCollection.name, docId);
      const doc =
        activeCollection?.name === 'users'
          ? sanitizeUserDocForEditor(data.document || {})
          : data.document || {};
      setEditorJson(JSON.stringify(doc, null, 2));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load document');
      setEditorOpen(false);
    }
  };

  const saveEditor = async () => {
    let parsed;
    try {
      parsed = JSON.parse(editorJson);
    } catch {
      toast.error('Invalid JSON');
      return;
    }
    if (activeCollection?.name === 'users') {
      delete parsed.password;
      delete parsed.password_hash;
      delete parsed.pin;
      delete parsed.pin_hash;
      const nextPass = userNewPassword.trim();
      if (nextPass) {
        if (nextPass.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
        parsed.password = nextPass;
      }
    }
    setEditorSaving(true);
    try {
      if (editorMode === 'create') {
        await createAdminDocument(activeCollection.name, parsed);
        toast.success('Document created');
      } else {
        await updateAdminDocument(activeCollection.name, editorDocId, parsed);
        toast.success('Document updated');
      }
      setEditorOpen(false);
      setUserNewPassword('');
      await loadTable();
      await loadCollections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setEditorSaving(false);
    }
  };

  const confirmDelete = (row) => {
    const docId = docIdFromRow(row, idField);
    if (!docId) return;
    setDeleteTarget({ id: docId, label: docId });
    setDeleteOpen(true);
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdminDocument(activeCollection.name, deleteTarget.id);
      toast.success('Document deleted');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await loadTable();
      await loadCollections();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const roleBadge = useMemo(() => {
    if (me?.role === 'admin') return <Badge className="bg-violet-600">Admin</Badge>;
    if (me?.role === 'moderator') return <Badge className="bg-amber-600">Moderator</Badge>;
    return null;
  }, [me?.role]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] px-3 sm:px-4 pb-16 overflow-x-hidden">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Database className="h-7 w-7 text-indigo-500" />
            Admin Dashboard
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Browse and manage MongoDB collections. Signed in as {me?.email} {roleBadge}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={adminView === 'collections' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setAdminView('collections')}
          >
            <Database className="h-4 w-4 mr-1" />
            Collections
          </Button>
          <Button
            type="button"
            variant={adminView === 'audit' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setAdminView('audit')}
            data-testid="admin-view-pipeline-audit"
          >
            <Activity className="h-4 w-4 mr-1" />
            Pipeline audit
          </Button>
          <Button
            type="button"
            variant={adminView === 'journey' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setAdminView('journey')}
            data-testid="admin-view-story-journey"
          >
            <Route className="h-4 w-4 mr-1" />
            Story journey
          </Button>
          <Button
            type="button"
            variant={adminView === 'customers' ? 'default' : 'outline'}
            size="sm"
            className="rounded-full"
            onClick={() => setAdminView('customers')}
            data-testid="admin-view-customer-journey"
          >
            <Users className="h-4 w-4 mr-1" />
            Customer journey
          </Button>
          {me?.role === 'admin' && (
            <Button
              type="button"
              variant={adminView === 'billing' ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => setAdminView('billing')}
            >
              <Coins className="h-4 w-4 mr-1" />
              Billing & credits
            </Button>
          )}
          <span className="flex items-center gap-2 text-xs text-slate-500">
            <Shield className="h-4 w-4" />
            {perms.delete ? 'Full CRUD' : 'Create · Read · Update'}
          </span>
        </div>
      </div>

      {adminView === 'billing' && me?.role === 'admin' ? (
        <AdminBillingPanel embedded />
      ) : adminView === 'audit' ? (
        <AdminPipelineAuditPanel />
      ) : adminView === 'journey' ? (
        <AdminStoryJourneyPanel />
      ) : adminView === 'customers' ? (
        <AdminCustomerJourneyPanel />
      ) : (
      <div className="flex min-h-[70vh] gap-4">
        <aside className="glass-panel w-56 shrink-0 rounded-2xl p-3">
          <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Collections
          </p>
          <nav className="max-h-[70vh] space-y-0.5 overflow-y-auto">
            {collections.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => {
                  setActiveCollection(c);
                  setPage(1);
                  setSearch('');
                  setSearchInput('');
                }}
                className={`flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                  activeCollection?.name === c.name
                    ? 'bg-indigo-500/15 font-medium text-indigo-700'
                    : 'hover:bg-slate-100/80'
                }`}
              >
                <span className="truncate">{c.label}</span>
                <span className="ml-1 shrink-0 text-xs text-slate-400">{c.count ?? 0}</span>
              </button>
            ))}
          </nav>
        </aside>

        <section className="glass-panel min-w-0 flex-1 rounded-2xl p-4">
          {activeCollection ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="font-heading text-lg font-semibold">{activeCollection.label}</h2>
                <span className="text-xs text-slate-400">{activeCollection.name}</span>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setPage(1);
                      setSearch(searchInput.trim());
                    }}
                  >
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        className="h-9 w-48 pl-8"
                        placeholder="Search…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                    <Button type="submit" variant="secondary" size="sm">
                      Search
                    </Button>
                  </form>
                  <Button variant="outline" size="sm" onClick={() => loadTable()}>
                    <RefreshCw className="mr-1 h-4 w-4" />
                    Refresh
                  </Button>
                  {perms.create && (
                    <Button size="sm" onClick={openCreate}>
                      <Plus className="mr-1 h-4 w-4" />
                      Create
                    </Button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-slate-200/80">
                {tableLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {columns.map((col) => (
                          <TableHead key={col} className="whitespace-nowrap text-xs">
                            {col}
                          </TableHead>
                        ))}
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length + 1} className="py-12 text-center text-slate-500">
                            No documents found
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row, idx) => (
                          <TableRow key={docIdFromRow(row, idField) || idx}>
                            {columns.map((col) => (
                              <TableCell key={col} className="max-w-[220px] truncate text-xs">
                                {cellPreview(row[col])}
                              </TableCell>
                            ))}
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {perms.update && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                                {perms.delete && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700"
                                    onClick={() => confirmDelete(row)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
                <span>
                  {total} document{total === 1 ? '' : 's'}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    Page {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <p className="py-12 text-center text-slate-500">Select a collection</p>
          )}
        </section>
      </div>
      )}

      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setUserNewPassword('');
        }}
      >
        <DialogContent className="admin-editor-dialog max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="pricing-heading">
              {editorMode === 'create' ? 'Create document' : `Edit ${editorDocId}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {activeCollection?.name === 'users' && (
              <div className="admin-editor-panel space-y-2 p-3">
                <Label htmlFor="admin-user-new-password">Set new password (optional)</Label>
                <Input
                  id="admin-user-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={userNewPassword}
                  onChange={(e) => setUserNewPassword(e.target.value)}
                  placeholder="Leave blank to keep current password"
                  className="max-w-md font-mono text-sm"
                  data-testid="admin-user-new-password"
                />
                <p className="admin-editor-hint">
                  Stored hashed on save. Do not put passwords in the JSON below.
                </p>
              </div>
            )}
            <Label>JSON document</Label>
            <textarea
              className="admin-json-textarea"
              value={editorJson}
              onChange={(e) => setEditorJson(e.target.value)}
              spellCheck={false}
              autoComplete="off"
            />
            {activeCollection?.name === 'users' && me?.role === 'admin' ? (
              <p className="admin-editor-hint">
                Only admins can change <code>role</code> (e.g. <code>admin</code>, <code>moderator</code>, <code>user</code>).
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button className="btn-primary-theme" onClick={saveEditor} disabled={editorSaving}>
              {editorSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="admin-editor-dialog">
          <DialogHeader>
            <DialogTitle className="pricing-heading">Delete document?</DialogTitle>
          </DialogHeader>
          <p className="admin-editor-hint text-sm">
            Permanently delete <strong className="text-[var(--text-heading)]">{deleteTarget?.label}</strong> from {activeCollection?.label}?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={runDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
